import { zActionCue, zSceneSetup } from "./storyActions";
import { z } from "zod";
import type { DatabaseRecord } from "./schema";
import { zPreparedSceneActivities, zSceneActivityContent } from "./storyActivities";
import { zStoryItem } from "./storyItems";
import { zStoryReview } from "./storyReview";
import {
  zCharacterVoiceProfile,
  zPerformanceSpeech,
  zStoryAudioDelivery,
  type PerformanceSpeech,
} from "./performanceSpeech";
import { zStoryFraming } from "./storyFraming";
import { zStoryTemporalFrames } from "./storyTemporalFrames";
import { zSceneAudioManifest } from "./sceneAudio";
import { zStoryWorldBinding, zWorldContext, zWorldState, zWorldSceneStart, zWorldOccurrenceRef } from "./worlds";
import { zStoryAssetCreation, type StoryAssetResult, type StoryWorldUpdate } from "./storyAssets";
import { zStoryResponseInput } from "./storyResponseInput";
import { zStoryDestination, type StoryDestination } from "./storyInteraction";
import { zStoryRepresentations } from "./storyRepresentations";
import { zChoreo, type Choreo } from "./choreo";
import { zBackdrop } from "./backdrops";
import { zStoryCustomization } from "./storyCustomization";
import { zStorySourceImage } from "./storyCustomization";
import { zStoryDelivery } from "./storyDelivery";
import { zImageReference } from "./imageReferenceSchema";

/** Stable document-safe identifiers also make bundles portable across projects. */
export const zPerformanceId = z
  .string()
  .min(1)
  .max(100)
  .regex(/^[a-zA-Z0-9_-]+$/);
const text = z.string().max(16000);
export { zStoryAction as zStoryCue } from "./storyActions";
// Persistence must retain unfinished authoring. The strict action-cue schema
// remains the generation/playback diagnostic contract, not a save gate.
export const zStoryBeat = zActionCue.innerType();
export type StoryBeat = z.infer<typeof zStoryBeat>;
export const zStoryBeatRecord = z.object({ sequence: z.number().int().nonnegative(), beat: zStoryBeat });
export type StoryBeatRecord = z.infer<typeof zStoryBeatRecord>;

/** A response's waiting performance is replayable without becoming part of story continuity. */
const transitionMs = z.number().int().nonnegative().max(600_000);
export const zStoryResponseTransitionPlayback = z
  .object({
    durationMs: transitionMs,
    beats: z
      .array(z.object({ beatId: zPerformanceId, startMs: transitionMs, endMs: transitionMs.optional() }).strict())
      .max(32),
  })
  .strict()
  .superRefine((playback, ctx) => {
    const ids = new Set<string>();
    let previousStart = 0;
    playback.beats.forEach((beat, index) => {
      if (
        ids.has(beat.beatId) ||
        beat.startMs < previousStart ||
        beat.startMs > playback.durationMs ||
        (beat.endMs !== undefined && (beat.endMs < beat.startMs || beat.endMs > playback.durationMs))
      )
        ctx.addIssue({
          code: "custom",
          path: ["beats", index],
          message: "Playback must contain unique, ordered beat times inside its duration.",
        });
      ids.add(beat.beatId);
      previousStart = beat.startMs;
    });
  });
export type StoryResponseTransitionPlayback = z.infer<typeof zStoryResponseTransitionPlayback>;
export const zStoryResponseTransition = z
  .object({
    id: zPerformanceId,
    /** Last ordinary beat already performed before the transition; omitted means before the first ordinary beat. */
    afterBeatId: zPerformanceId.optional(),
    source: z.enum(["generated", "legacy-generated"]),
    /** Omitted on older waiting scores. Recovery explains a failed turn without becoming story content. */
    purpose: z.enum(["waiting", "recovery"]).optional(),
    timing: z.enum(["server", "unrecorded"]).default("server"),
    status: z.enum(["streaming", "complete", "failed", "cancelled"]),
    startedAt: z.number().int().nonnegative().optional(),
    setup: zSceneSetup,
    framing: zStoryFraming.optional(),
    beats: z
      .array(
        z
          .object({
            offsetMs: transitionMs,
            beat: zStoryBeat.refine(
              (beat) => !beat.interaction && !beat.actions.some((action) => action.kind === "item"),
              "Response transition beats cannot invite answers or change story inventory."
            ),
          })
          .strict()
      )
      .max(32),
    durationMs: transitionMs.optional(),
    /** First client-observed experience, including pauses after the last waiting beat. */
    playback: zStoryResponseTransitionPlayback.optional(),
  })
  .strict()
  .superRefine((transition, ctx) => {
    if (transition.source === "generated" && transition.startedAt === undefined)
      ctx.addIssue({
        code: "custom",
        path: ["startedAt"],
        message: "A newly generated transition needs its start timestamp.",
      });
    let previousOffset = 0;
    const ids = new Set<string>();
    transition.beats.forEach(({ offsetMs, beat }, index) => {
      if (
        offsetMs < previousOffset ||
        ids.has(beat.id) ||
        (transition.durationMs !== undefined && offsetMs > transition.durationMs)
      )
        ctx.addIssue({
          code: "custom",
          path: ["beats", index],
          message: "Transition beats need unique ids and ordered offsets inside the duration.",
        });
      ids.add(beat.id);
      previousOffset = offsetMs;
    });
    let previousIndex = -1;
    transition.playback?.beats.forEach((entry, index) => {
      const beatIndex = transition.beats.findIndex(({ beat }) => beat.id === entry.beatId);
      if (beatIndex <= previousIndex)
        ctx.addIssue({
          code: "custom",
          path: ["playback", "beats", index],
          message: "Observed playback must follow the saved transition's beat order.",
        });
      previousIndex = beatIndex;
    });
  });
export type StoryResponseTransition = z.infer<typeof zStoryResponseTransition>;

/** How many named sequences one cast member may carry for a show. */
export const MAX_CAST_CHOREOS = 16;
/**
 * A cast member's own named pose sequences for this show (docs/choreo-proposal.md, "Where choreos live", item 2):
 * played by id like a sheet's, and ahead of any sheet's of the same id. Each needs an id, since nothing can name one
 * without it, and ids are unique per member. Poses may come from any of the character's sheets.
 */
export const zCastChoreos = z
  .array(zChoreo)
  .max(MAX_CAST_CHOREOS)
  .superRefine((choreos, ctx) => {
    const ids = new Set<string>();
    choreos.forEach((choreo, index) => {
      if (!choreo.id)
        ctx.addIssue({ code: "custom", path: [index, "id"], message: "Name each of the cast's sequences with an id." });
      else if (ids.has(choreo.id))
        ctx.addIssue({ code: "custom", path: [index, "id"], message: `The sequence id "${choreo.id}" is used twice.` });
      if (choreo.id) ids.add(choreo.id);
    });
  });

/**
 * The cast member's playable sequences, for readers that must not fail on one bad entry (the stager's offer, the
 * player, a shared stage): an entry that does not parse or has no id is skipped, and the first of a repeated id kept.
 */
export function castMemberChoreos(member: { choreos?: unknown } | null | undefined): Choreo[] {
  const listed = member?.choreos;
  if (!Array.isArray(listed)) return [];
  const out = new Map<string, Choreo>();
  for (const entry of listed) {
    const parsed = zChoreo.safeParse(entry);
    if (parsed.success && parsed.data.id && !out.has(parsed.data.id) && out.size < MAX_CAST_CHOREOS)
      out.set(parsed.data.id, parsed.data);
  }
  return [...out.values()];
}

export const zStoryCastMember = z
  .object({
    id: zPerformanceId,
    name: z.string().min(1).max(200),
    /** Character document source; omitted values inherit the performance owner. */
    accountId: zPerformanceId.optional(),
    animalId: zPerformanceId.optional(),
    description: text.optional(),
    /** Timeless delivery/personality guidance; discoveries belong in scene checkpoints. */
    voice: z.string().max(6000).optional(),
    voiceProfile: zCharacterVoiceProfile.optional(),
    personaPath: z.string().max(500).optional(),
    /** The selected persona's immutable ID. All new assigned cast writes must provide it. */
    personaId: zPerformanceId.optional(),
    /** Stable story appearance key -> persona ID; labels in legacy variants never override these IDs. */
    variantPersonaIds: z.record(zPerformanceId).optional(),
    /** Optional persona display label. Never used to select a resource. */
    jsonName: z.string().max(200).optional(),
    /** Optional appearance labels. Every key selects its ID through variantPersonaIds. */
    variants: z.record(z.string().max(200)).optional(),
    /** Keep the authored role and cues while its character is removed or retired. */
    unassigned: z.boolean().optional(),
    inventoryContainer: z.string().min(1).max(100).optional(),
    /** Show-specific named sequences ("Sequences" in the editor); absent on stories saved before them. */
    choreos: zCastChoreos.optional(),
  })
  .passthrough();

/** Legacy portable casts may identify their source through a persona path. */
export function storyCastAccountId(
  character: { accountId?: string; personaPath?: string },
  performanceAccountId: string
): string {
  const legacyAccount = /^Account\/([^/]+)\/Animal\/[^/]+$/.exec(character.personaPath || "")?.[1];
  return zPerformanceId.parse(character.accountId || legacyAccount || performanceAccountId);
}
export const zStoryWorld = z.object({
  synopsis: z.string().max(12000).default(""),
  plotPoints: z.array(z.string().max(1000)).max(40).default([]),
  openThreads: z.array(z.string().max(1000)).max(40).default([]),
  inventory: z.array(z.string().max(500)).max(100).default([]),
  /** Exact checkpoint from item cues. Legacy inventory prose remains readable. */
  items: z.array(zStoryItem).max(200).optional(),
  characters: z
    .array(
      z.object({
        id: zPerformanceId,
        location: z.string().max(300),
        backstory: z.string().max(3000),
        motivation: z.string().max(1000).optional(),
      })
    )
    .max(60)
    .default([]),
});
export type StoryWorld = z.infer<typeof zStoryWorld>;
export const zStoryMemory = z.object({
  id: zPerformanceId,
  kind: z.enum(["plot", "thread", "inventory", "character", "location"]),
  text: z.string().min(1).max(6000),
  characterId: zPerformanceId.optional(),
  status: z.enum(["active", "resolved", "historical"]).default("active"),
  tags: z.array(z.string().max(80)).max(12).default([]),
});
export type StoryMemory = z.infer<typeof zStoryMemory>;

export const zStorySceneHeader = z
  .object({
    timelineStart: zWorldSceneStart.optional(),
    portrays: zWorldOccurrenceRef.optional(),
    id: zPerformanceId,
    act: z.string().max(200),
    title: z.string().min(1).max(300),
    // Explicitly reject v1 opening fields even on persisted, extensible records.
    backdrop: z.never().optional(),
    castAtStart: z.never().optional(),
    camera: z.never().optional(),
    itemsAtStart: z.array(zStoryItem).max(200).optional(),
    transition: z.enum(["continue", "blackout", "curtain", "shift-up", "shift-down"]).optional(),
    travellers: z.array(zPerformanceId).max(60).optional(),
    /** Independently staged views of existing characters; never additions to the story cast. */
    representations: zStoryRepresentations.optional(),
    /** Enacted memories and imagined scenarios are distinct from the branch's present continuity. */
    narrativeMode: z.enum(["present", "flashback", "imagined"]).optional(),
    temporalFrames: zStoryTemporalFrames.optional(),
    /** Independently playable, pre-generated clips keyed by beat and spoken action. */
    speech: zPerformanceSpeech.optional(),
    /** The scene's synchronized audio timeline (music, transcript, beat anchors, mix). See sceneAudio.ts. */
    audio: zSceneAudioManifest.optional(),
    /** Saved editor brief, before there is a recording or a playable audio timeline. */
    musicDraft: z
      .object({
        mode: z.enum(["musical", "music-video"]),
        brief: z.string().max(4000),
        styleId: z.string().max(100).optional(),
        targetDurationMs: z.number().int().min(30000).max(180000),
        singers: z.array(zPerformanceId).max(2),
      })
      .strict()
      .optional(),
    setup: zSceneSetup.optional(),
    /** Generated stage composition. Older authored scenes retain their existing camera behavior when omitted. */
    framing: zStoryFraming.optional(),
    responseTransition: zStoryResponseTransition.optional(),
    /** Host listening after the adapted character has spoken, separate from finding their words. */
    responseAnswerTransition: zStoryResponseTransition.optional(),
    responseRecoveryTransition: zStoryResponseTransition.optional(),
    /** Private original submission; audience playback uses only its performed beats. */
    responseInput: zStoryResponseInput.optional(),
    /** Rejoin an authored scene after this response has finished playing. */
    continueTo: zStoryDestination.optional(),
  })
  .passthrough();
export type StorySceneHeader = z.infer<typeof zStorySceneHeader>;
export const zStoryScene = zStorySceneHeader.extend({
  setup: zSceneSetup.innerType().optional(),
  branchId: zPerformanceId,
  sequence: z.number().int().nonnegative(),
  parentSceneId: zPerformanceId.optional(),
  revisionOf: zPerformanceId.optional(),
  status: z.enum(["streaming", "complete", "failed", "cancelled"]),
  beatCount: z.number().int().nonnegative(),
  summary: text.optional(),
  suggestions: z.array(z.string().max(500)).max(6).default([]),
  world: zStoryWorld.optional(),
  worldState: zWorldState.optional(),
  worldContext: zWorldContext.optional(),
  sourceId: zPerformanceId.optional(),
  worldProposalId: zPerformanceId.optional(),
  worldCommitId: zPerformanceId.optional(),
  rememberingStatus: z.enum(["remembered", "exploring", "needs-choice"]).optional(),
  timeframe: z.enum(["past", "present", "future"]).optional(),
  requestId: zPerformanceId.optional(),
});
export type StoryScene = z.infer<typeof zStoryScene> & DatabaseRecord;
export const zStoryBranch = z.object({
  id: zPerformanceId,
  name: z.string().max(200),
  headSceneId: zPerformanceId.optional(),
  baseSceneId: zPerformanceId.optional(),
  /** Invitation beat in the generated scene's revisionOf source for a mid-scene rewrite; in baseSceneId for an end continuation. */
  baseBeatId: zPerformanceId.optional(),
  parentBranchId: zPerformanceId.optional(),
  sceneCount: z.number().int().nonnegative().default(0),
  world: zStoryWorld.default({}),
  worldContext: zWorldContext.optional(),
});
export type StoryBranch = z.infer<typeof zStoryBranch> & DatabaseRecord;
/** Audience navigation metadata; sceneIds are ordered across branch boundaries, never by local sequence. */
export interface StoryPlaybackPath {
  id: string;
  /** A neutral label, never the generation direction stored in a branch name. */
  name: string;
  sceneIds: string[];
  forkSceneId?: string;
  forkBeatId?: string;
  firstSceneId?: string;
  parentBranchId?: string;
  /** A bounded excerpt of the original submission, only for its owner or an administrator. */
  answerPreview?: string;
}
export const zPerformance = z
  .object({
    id: zPerformanceId,
    /** Canonical path owner in API responses; servers replace client input. */
    accountId: zPerformanceId.optional(),
    schemaVersion: z.literal(4),
    title: z.string().min(1).max(300),
    description: text.default(""),
    image: zImageReference.optional(),
    imageSource: z.literal("opening-scene").optional(),
    sourcePrompt: z.string().max(16000).optional(),
    /** Older workflows retained only the approved brief; recovered text must not masquerade as the typed request. */
    sourcePromptRecovered: z.boolean().optional(),
    sourceImages: z.array(zStorySourceImage).max(6).optional(),
    /** Author-reviewed, spoiler-free tone and characterization shared by this production. */
    writingGuide: z.string().max(12000).optional(),
    /** Optional audience navigation for repeatable routines; destinations are authored scenes. */
    routine: z
      .object({
        title: z.string().min(1).max(40),
        pathId: zPerformanceId,
        steps: z
          .array(
            z.object({
              id: zPerformanceId,
              label: z.string().min(1).max(40),
              icon: z.string().min(1).max(8),
              sceneId: zPerformanceId,
              /** Keeps the current step accurate in generated scenes in the same setting. */
              backdropId: z.string().min(1).max(200),
            })
          )
          .min(2)
          .max(12),
      })
      .optional(),
    customization: zStoryCustomization.optional(),
    delivery: zStoryDelivery.optional(),
    review: zStoryReview.optional(),
    status: z.enum(["draft", "published", "archived"]).default("draft"),
    cast: z
      .array(
        zStoryCastMember.refine(
          (role) =>
            role.unassigned ||
            !(role.animalId || role.personaPath) ||
            (!!role.personaId && Object.keys(role.variants || {}).every((key) => !!role.variantPersonaIds?.[key])),
          { message: "Assigned cast requires a persona ID." }
        )
      )
      .max(100),
    /** Ordered backdrop identities; none of these IDs imply an image filename. */
    backdropIds: z.array(z.string().max(200)).max(500),
    backdropSlugs: z.never().optional(),
    assetBase: z.never().optional(),
    defaultBranchId: zPerformanceId.default("main"),
    backdrops: z.record(zBackdrop).default({}),
    /** Account Items (same account as the performance) that belong to this story's world -- props, scenery, wearables. */
    worldItemIds: z.array(zPerformanceId).max(500).optional(),
    worldBinding: zStoryWorldBinding.optional(),
    authoringSource: z.never().optional(),
    sourcePerformanceId: zPerformanceId.optional(),
    sourceAccountId: zPerformanceId.optional(),
  })
  .passthrough();
export type Performance = z.infer<typeof zPerformance> & DatabaseRecord;

/** Only finished reusable content is portable; worker leases are environment-local. */
export const zReadyStoryActivities = zPreparedSceneActivities
  .extend({
    status: z.literal("ready"),
    content: zSceneActivityContent,
    /** Exact captured scene version; title/art labels are never version identities. */
    sourceId: zPerformanceId.optional(),
  })
  .omit({ error: true });

export function storyActivitySourceText(scene: Record<string, unknown>, beats: Array<Record<string, unknown>>) {
  const withoutMetadata = ({
    created: _created,
    updated: _updated,
    speech: _speech,
    responseTransition: _responseTransition,
    responseAnswerTransition: _responseAnswerTransition,
    responseRecoveryTransition: _responseRecoveryTransition,
    responseInput: _responseInput,
    ...data
  }: Record<string, unknown>) => data;
  const normalized = JSON.parse(
    JSON.stringify({
      scene: withoutMetadata(zStoryScene.parse(scene)),
      beats: z
        .array(zStoryBeatRecord)
        .parse(beats)
        .sort((a, b) => a.sequence - b.sequence)
        .map(withoutMetadata),
    })
  );
  const ordered = (value: any): any =>
    Array.isArray(value)
      ? value.map(ordered)
      : value && typeof value === "object"
        ? Object.fromEntries(
            Object.keys(value)
              .sort()
              .map((key) => [key, ordered(value[key])])
          )
        : value;
  return JSON.stringify(ordered(normalized));
}
const archiveId = z
  .string()
  .min(1)
  .max(200)
  .regex(/^[a-zA-Z0-9_-]+$/);
export const zStoryActivityAnswerArchive = z.object({
  id: archiveId,
  questionId: archiveId,
  uid: z.string().min(1).max(128),
  characterId: zPerformanceId,
  choice: z.number().int().min(0).max(3),
  performerName: z.string().max(200),
  answeredAt: z.number().finite().nonnegative(),
  userAgent: z
    .object({
      name: z.string().max(200),
      userId: z.string().max(128),
      accountId: zPerformanceId.optional(),
      mindId: zPerformanceId.optional(),
      mindPath: z.string().max(500).optional(),
    })
    .optional(),
});
/** Historical responses belong to private exports; they never grant a live screen membership. */
export const zStoryActivityRunArchive = z
  .object({
    id: zPerformanceId,
    mode: z.enum(["quiz", "jokes"]),
    content: zSceneActivityContent,
    questionIndex: z.number().int().min(0).max(3),
    startedAt: z.number().finite().nonnegative(),
    updatedAt: z.number().finite().nonnegative(),
    endedAt: z.number().finite().nonnegative().optional(),
    answers: z.array(zStoryActivityAnswerArchive),
  })
  .superRefine((run, context) => {
    if (run.questionIndex >= run.content.questions.length)
      context.addIssue({ code: z.ZodIssueCode.custom, message: "Activity run question is missing" });
    const ids = new Set<string>();
    for (const answer of run.answers) {
      const question = run.content.questions.find((entry) => entry.id === answer.questionId);
      if (ids.has(answer.id) || !question || answer.choice >= question.choices.length)
        context.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Activity answer is duplicate or references an unavailable choice",
        });
      ids.add(answer.id);
    }
  });
export type StoryActivityRunArchive = z.infer<typeof zStoryActivityRunArchive>;

/** Restore an inert historical run, rebased to its destination story without old session IDs. */
export function restoreStoryActivityRun(
  archive: StoryActivityRunArchive,
  source: { accountId: string; performanceId: string; sceneId: string }
) {
  const { answers, ...run } = archive;
  const question = run.content.questions[run.questionIndex];
  const counts = question.choices.map(
    (_, choice) => answers.filter((answer) => answer.questionId === question.id && answer.choice === choice).length
  );
  return {
    ...run,
    source,
    status: "ended" as const,
    archived: true,
    questionCount: run.content.questions.length,
    question,
    revealed: true,
    correctChoice: question.correctChoice,
    explanation: question.explanation,
    counts,
    totalVotes: counts.reduce((sum, count) => sum + count, 0),
    endedAt: run.endedAt ?? run.updatedAt,
  };
}

/** Authoring artifacts are private to the story owner and administrators, never audience playback data. */
export const zStoryArtifact = z.object({
  id: zPerformanceId,
  kind: z.literal("screenplay"),
  status: z.enum(["streaming", "complete", "error"]),
  format: z.literal("text/plain"),
  title: z.string().min(1).max(300),
  content: z.string().max(128000),
  model: z.string().max(200).optional(),
  createdAt: z.number().finite().nonnegative().optional(),
  completedAt: z.number().finite().nonnegative().optional(),
  error: z.string().max(2000).optional(),
});
export type StoryArtifact = z.infer<typeof zStoryArtifact>;

export const zPerformanceBundle = z.object({
  schemaVersion: z.literal(4),
  performance: zPerformance,
  scenes: z.array(
    z
      .object({
        scene: zStoryScene,
        beats: z.array(zStoryBeatRecord),
        memories: z.array(zStoryMemory).optional(),
        activities: zReadyStoryActivities.optional(),
        activityRuns: z.array(zStoryActivityRunArchive).optional(),
        artifacts: z.array(zStoryArtifact).max(20).optional(),
      })
      .superRefine((entry, context) => {
        if (entry.activities && entry.activities.sceneId !== entry.scene.id)
          context.addIssue({ code: z.ZodIssueCode.custom, message: "Prepared activities reference another scene" });
        if (entry.activities && entry.scene.status !== "complete")
          context.addIssue({ code: z.ZodIssueCode.custom, message: "Prepared activities require a complete scene" });
        if (new Set(entry.activityRuns?.map((run) => run.id)).size !== (entry.activityRuns?.length ?? 0))
          context.addIssue({ code: z.ZodIssueCode.custom, message: "Duplicate activity run id" });
        if (new Set(entry.artifacts?.map((artifact) => artifact.id)).size !== (entry.artifacts?.length ?? 0))
          context.addIssue({ code: z.ZodIssueCode.custom, message: "Duplicate story artifact id" });
      })
  ),
  branches: z.array(zStoryBranch).optional(),
  branchMemories: z.array(z.object({ branchId: zPerformanceId, memories: z.array(zStoryMemory) })).optional(),
});
export type PerformanceBundle = z.infer<typeof zPerformanceBundle>;

export const zStoryReadingLevel = z.enum(["original", "simple", "very-simple"]);
export type StoryReadingLevel = z.infer<typeof zStoryReadingLevel>;

export const zStorySceneLength = z.enum(["short", "medium", "long"]);
export type StorySceneLength = z.infer<typeof zStorySceneLength>;
export const zStoryPresentationStyle = z.enum(["story-theatre", "drama", "playful"]);
export type StoryPresentationStyle = z.infer<typeof zStoryPresentationStyle>;

export const zStreamPerformanceRequest = z.object({
  accountId: zPerformanceId,
  performanceId: zPerformanceId,
  branchId: zPerformanceId.default("main"),
  requestId: zPerformanceId,
  prompt: z.string().trim().min(1).max(6000),
  anchorSceneId: zPerformanceId.optional(),
  /** Narrative words are resolved against pinned world evidence before writing. */
  anchorDescription: z.string().trim().min(1).max(1000).optional(),
  timelineStart: zWorldSceneStart.optional(),
  timeframe: z.enum(["past", "present", "future"]).default("present"),
  sceneLength: zStorySceneLength.default("medium"),
  presentationStyle: zStoryPresentationStyle.default("story-theatre"),
  framing: zStoryFraming.optional(),
  /** Omitted/off is silent. Delivery affects playback, never the generated story request identity. */
  audioDelivery: zStoryAudioDelivery.optional(),
  /** Transport capability only; omitted clients receive the original event vocabulary. */
  sceneTitleUpdates: z.boolean().optional(),
  feedback: z
    .object({
      sceneId: zPerformanceId,
      afterBeatId: zPerformanceId.optional(),
      mode: z.enum(["continue", "restart"]),
      text: z.string().trim().min(1).max(6000),
    })
    .optional(),
  focusMemoryIds: z.array(zPerformanceId).max(20).optional(),
  /** What the generator may create for this scene (new pose sheets, characters, items, backdrops, variations). */
  assetCreation: zStoryAssetCreation.optional(),
  /** Active readers only; keyed by assigned cast id, never a private profile. */
  readerLevels: z
    .record(zPerformanceId, zStoryReadingLevel)
    .refine((levels) => Object.keys(levels).length <= 100)
    .optional(),
});
export type StreamPerformanceRequest = z.input<typeof zStreamPerformanceRequest>;
export type StoryStreamEvent =
  | { type: "status"; requestId: string; message: string }
  | { type: "artifact-start"; requestId: string; branchId: string; sceneId: string; artifact: StoryArtifact }
  | {
      type: "artifact-delta";
      requestId: string;
      branchId: string;
      sceneId: string;
      artifactId: string;
      sequence: number;
      text: string;
    }
  | { type: "artifact-end"; requestId: string; branchId: string; sceneId: string; artifact: StoryArtifact }
  | { type: "scene-start"; requestId: string; branchId: string; sceneId: string; scene: StorySceneHeader }
  | {
      /** Display metadata only; updates a placeholder without rewriting any playable beats. */
      type: "scene-title";
      requestId: string;
      branchId: string;
      sceneId: string;
      title: string;
    }
  | { type: "beat"; requestId: string; branchId: string; sceneId: string; sequence: number; beat: StoryBeat }
  | {
      /** Sealed saved clips for this exact beat, or a truthful unavailable result. */
      type: "speech-ready";
      requestId: string;
      branchId: string;
      sceneId: string;
      beatId: string;
      speech: PerformanceSpeech;
      generated: number;
      cached: number;
      skipped: number;
      errors: Array<{ lineId: string; message: string }>;
    }
  | {
      type: "response-transition-start";
      requestId: string;
      branchId: string;
      sceneId: string;
      transition: StoryResponseTransition;
    }
  | {
      type: "response-transition-beat";
      requestId: string;
      branchId: string;
      sceneId: string;
      transitionId: string;
      sequence: number;
      offsetMs: number;
      beat: StoryBeat;
    }
  | {
      type: "response-transition-end";
      requestId: string;
      branchId: string;
      sceneId: string;
      transitionId: string;
      status: StoryResponseTransition["status"];
      durationMs: number;
    }
  | {
      type: "scene-end";
      requestId: string;
      branchId: string;
      sceneId: string;
      summary: string;
      suggestions: string[];
      continueTo?: StoryDestination;
    }
  | {
      /** All playable content is saved. Async recorded speech may still be arriving. */
      type: "content-complete";
      requestId: string;
      branchId: string;
      sceneId: string;
      sceneIds?: string[];
    }
  | {
      type: "complete";
      requestId: string;
      branchId: string;
      /** Final saved head. */
      sceneId: string;
      /** Generated scenes in playback order; absent on older single-scene streams. */
      sceneIds?: string[];
    }
  | { type: "error"; requestId: string; message: string; sceneId?: string; branchId?: string }
  /** Sent only to requests with assetCreation, before any beat uses what was made. */
  | {
      type: "world-update";
      requestId: string;
      branchId: string;
      sceneId: string;
      world: StoryWorldUpdate;
      assets: StoryAssetResult[];
    };
export interface StreamPerformanceResponse {
  performanceId: string;
  sceneId: string;
  /** Generated scenes in playback order. sceneId remains the final saved head. */
  sceneIds?: string[];
  branchId: string;
  beatCount: number;
  suggestions: string[];
  /** What the generator created for the scene (requests with assetCreation). */
  assets?: StoryAssetResult[];
}

export type ManagePerformanceRequest =
  | { action: "list"; accountId: string; limit?: number; afterId?: string; includeDrafts?: boolean }
  | { action: "getScene"; accountId: string; performanceId: string; sceneId: string }
  | {
      /** Load a complete saved path and compact alternate fork metadata for audience playback. */
      action: "playback";
      accountId: string;
      performanceId: string;
      /** The selected leaf remains selected when seeking one of its inherited scenes. */
      branchId?: string;
    }
  | {
      action: "get";
      accountId: string;
      performanceId: string;
      branchId?: string;
      limit?: number;
      beforeSequence?: number;
    }
  | { action: "create"; accountId: string; performance: z.input<typeof zPerformance> }
  | { action: "import"; accountId: string; bundle: PerformanceBundle; overwrite?: boolean }
  | { action: "export"; accountId: string; performanceId: string }
  | { action: "editor"; accountId: string; performanceId: string }
  | { action: "inspectBackdrop"; accountId: string; performanceId: string; backdropId: string }
  | { action: "removeBackdrop"; accountId: string; performanceId: string; backdropId: string; requestId: string }
  | {
      action: "saveEditor";
      accountId: string;
      performanceId: string;
      bundle: PerformanceBundle;
      editorVersion: string;
      allowIssues?: boolean;
    }
  | { action: "deleteScene"; accountId: string; performanceId: string; sceneId: string; editorVersion: string }
  | {
      /** Remove a private saved fork and its dependent paths from the active story graph. */
      action: "deletePath";
      accountId: string;
      performanceId: string;
      branchId: string;
      /** Exact editor snapshot used to review the paths being removed. */
      editorVersion: string;
    }
  | { action: "fork"; accountId: string; sourceAccountId: string; performanceId: string; newPerformanceId?: string }
  | {
      action: "recordResponseTransitionPlayback";
      accountId: string;
      performanceId: string;
      sceneId: string;
      transitionId: string;
      playback: StoryResponseTransitionPlayback;
    }
  | { action: "migrateResponseTransition"; accountId: string; performanceId: string; sceneId: string }
  | { action: "cancel"; accountId: string; performanceId: string; requestId: string };
export interface ManagePerformanceResponse {
  backdropUsage?: StoryBackdropUse[];
  backdropRemoved?: boolean;
  /** Exact saved scene, without substituting a newer branch checkpoint. */
  scene?: StoryScene;
  /** Private authoring drafts; omitted for public-library readers and shared audience screens. */
  artifacts?: StoryArtifact[];
  performance?: Performance;
  performances?: Performance[];
  scenes?: Array<{ scene: StoryScene; beats: StoryBeatRecord[] }>;
  branch?: StoryBranch;
  /** Present for playback; scenes are already in full selected-path ancestry order. */
  playbackPaths?: StoryPlaybackPath[];
  bundle?: PerformanceBundle;
  editorVersion?: string;
  nextCursor?: string;
  cancelled?: boolean;
  responseTransition?: StoryResponseTransition;
  /** Present after deletePath; inherited scenes and independent paths are preserved. */
  deletedBranchIds?: string[];
  deletedSceneIds?: string[];
  deletedRequestIds?: string[];
  /** Surviving parent path, or the protected default path. */
  fallbackBranchId?: string;
}

/** Stories that still need a backdrop, including alternate paths and unfinished scenes. */
export interface StoryBackdropUse {
  accountId: string;
  performanceId: string;
  title: string;
  scenes: Array<{ id: string; title: string }>;
  generating?: boolean;
}
import { zStageMark, stageMoveFields } from "./stageMovement";
